import React, { useEffect, useState } from 'react';
import Box from '~/components/Box';
import { Table, TableHeader } from '~/components/Table';
import { Card, Select, Table as AntdTable, Input } from 'antd';
import Global from '~/styles/global';
import { useTranslation } from 'react-i18next';
import Button from '~/components/Button';
import IsMobile from '~/services/mobileCheck';
import errorHandler from '~/Utils/errorHandler';
import { getFinancialExpenses } from '~/services/hooks/financialExpenses';
import { recordingExpensesColomns } from '~/tables/expenses';
import { CardTitle, CardTitleContainer, TitleTag } from './styles';
import { formatPrice } from '~/Utils/format';
import Can from '~/components/Can';
import { FaPlus } from 'react-icons/fa';
import FinancialExpenseItemExpensesModal from './financialExpenseItemExpensesModal';
import FinancialExpenseItemExpensesView from './financialExpenseItemExpensesView';

export default function RecordingExpenses() {
  const { t } = useTranslation();
  const isMobile = IsMobile();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [totalExpenseType, setTotalExpenseType] = useState([]);
  const [recordingExpenseModalVisible, setRecordingExpenseModalVisible] = useState(false);
  const [recordingFinancialEx, setRecordingFinancialEx] = useState(null);
  const [viewExpenseVisible, setViewExpenseVisible] = useState(false);
  const [viewFinancialExItem, setViewFinancialExItem] = useState(null);
  const years = Array.from({ length: 7 }, (_, i) => new Date().getFullYear() - i);
  const [selectedYear, setSelectedYear] = useState(years[0]);
  const [feiSearch, setFeiSearch] = useState('');
  const expenseTypes = {
    0: 'Fixa',
    1: 'Fixa variável',
    2: 'Variável',
  };

  const fetchFinancialEx = async () => {
    setLoading(true);
    try {
      const params = {
        params: {
          year: selectedYear,
          search: feiSearch,
        },
      };
      const data = await getFinancialExpenses(params, 'no-paginated');

      setData(data);
    } catch (error) {
      errorHandler(error);
    }
    setLoading(false);
  };

  const fetchTotalByExpenseType = async () => {
    setLoading(true);
    try {
      const params = {
        params: {
          year: selectedYear,
        },
      };
      const data = await getFinancialExpenses(params, 'total-by-expense-type');

      setTotalExpenseType(data);
    } catch (error) {
      errorHandler(error);
    }
    setLoading(false);
  };

  const handleEdit = (financialEx) => {
    setRecordingFinancialEx(financialEx);
    setRecordingExpenseModalVisible(true);
  };

  useEffect(() => {
    fetchFinancialEx();
    fetchTotalByExpenseType();
  }, [selectedYear, feiSearch]);

  return (
    <Box>
      <TableHeader>
        <Select defaultValue={selectedYear} style={{ width: 200 }} onChange={(value) => setSelectedYear(value)}>
          <Select.Option value="" disabled>
            Selecionar Ano
          </Select.Option>
          {years.map((year) => (
            <Select.Option key={year} value={year}>
              {year}
            </Select.Option>
          ))}
        </Select>
        <Input.Search
          style={{ width: 300, marginLeft: '10px' }}
          onSearch={(value) => {
            setFeiSearch(value);
          }}
          placeholder="Pesquisar"
        />
      </TableHeader>
      {data &&
        Object.keys(expenseTypes).map((typeKey) => {
          const type = Number(typeKey);
          const expensesList = data.filter((item) => item.expense_type === type);

          if (expensesList.length === 0) return null;

          return (
            <Card
              key={type}
              headStyle={{ padding: 0, borderBottom: 'none' }}
              style={{
                marginBottom: '40px',
                borderRadius: '8px',
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
              }}
              title={
                <CardTitleContainer>
                  <CardTitle>{expenseTypes[type]}</CardTitle>
                </CardTitleContainer>
              }
            >
              {expensesList.map((item) => (
                <React.Fragment key={item?.id}>
                  <TitleTag>
                    <span>{t(item?.name)}</span>
                    <Can permission="@eventsExpenses/edit">
                      <Button color="primary" size="small" title="Adicionar despesa" onClick={() => handleEdit(item)}>
                        <FaPlus /> <p>{t('Adicionar despesa')}</p>
                      </Button>
                    </Can>
                  </TitleTag>

                  <Table
                    size="middle"
                    pagination={false}
                    rowKey="id"
                    loading={loading}
                    dataSource={item?.financial_expense_items}
                    columns={recordingExpensesColomns(isMobile)}
                    bordered
                    onRow={(record) => {
                      return {
                        onClick: () => {
                          setViewFinancialExItem(record);
                          setViewExpenseVisible(record);
                        },
                      };
                    }}
                    summary={() => {
                      const totalSector = item?.financial_expense_items.reduce(
                        (acc, fei) => {
                          acc.tableTotal += parseFloat(fei.total);
                          return acc;
                        },
                        {
                          tableTotal: 0,
                        }
                      );

                      return (
                        <AntdTable.Summary.Row style={{ backgroundColor: '#d6d6d6' }}>
                          <AntdTable.Summary.Cell>{t('TOTAL')}</AntdTable.Summary.Cell>
                          {Array.from({ length: 12 }).map((_, index) => (
                            <AntdTable.Summary.Cell key={index} />
                          ))}
                          <AntdTable.Summary.Cell>{formatPrice(totalSector.tableTotal, false)}</AntdTable.Summary.Cell>
                        </AntdTable.Summary.Row>
                      );
                    }}
                  />
                </React.Fragment>
              ))}
            </Card>
          );
        })}
      <TitleTag>
        <span>{t('Total por tipo de despesa')}</span>
      </TitleTag>

      <Table
        size="middle"
        pagination={false}
        rowKey="id"
        loading={loading}
        dataSource={totalExpenseType}
        columns={recordingExpensesColomns(isMobile)}
        bordered
        summary={() => {
          const months = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];

          const totalSector = totalExpenseType.reduce(
            (acc, tet) => {
              acc.tableTotal += parseFloat(tet.total);

              months.forEach((month) => {
                acc[month] += parseFloat(tet[month]);
              });

              return acc;
            },
            {
              tableTotal: 0,
              jan: 0,
              fev: 0,
              mar: 0,
              abr: 0,
              mai: 0,
              jun: 0,
              jul: 0,
              ago: 0,
              set: 0,
              out: 0,
              nov: 0,
              dez: 0,
            }
          );

          return (
            <AntdTable.Summary.Row style={{ backgroundColor: '#d6d6d6', fontWeight: 'bold' }}>
              <AntdTable.Summary.Cell>{t('TOTAL')}</AntdTable.Summary.Cell>
              {months.map((month) => (
                <AntdTable.Summary.Cell key={month}>{formatPrice(totalSector[month], false)}</AntdTable.Summary.Cell>
              ))}
              <AntdTable.Summary.Cell>{formatPrice(totalSector.tableTotal, false)}</AntdTable.Summary.Cell>
            </AntdTable.Summary.Row>
          );
        }}
      />
      <Global />
      <FinancialExpenseItemExpensesModal
        visible={recordingExpenseModalVisible}
        financialEx={recordingFinancialEx}
        isMobile={isMobile}
        onClose={() => {
          setRecordingFinancialEx(null);
          setRecordingExpenseModalVisible(false);
          fetchFinancialEx();
          fetchTotalByExpenseType();
        }}
      />
      <FinancialExpenseItemExpensesView
        visible={viewExpenseVisible}
        financialExItem={viewFinancialExItem}
        year={selectedYear}
        isMobile={isMobile}
        onClose={() => {
          setViewFinancialExItem(null);
          setViewExpenseVisible(false);
          fetchFinancialEx();
          fetchTotalByExpenseType();
        }}
      />
    </Box>
  );
}
